import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import _ from "lodash";
import cx from "classnames";
import { Button, Form } from "react-bootstrap";

import styles from "./resetPassword.module.scss";

import AnonymousPageView from "../common/AnonymousPageView";

import { resetPassword, validatingRecoveryLink } from "actions/users";
import cleverStoryLogo from "../../../assets/images/logo/logo-cs.svg";

export default function ResetPassword() {
  const { id } = useParams();
  const location = useLocation();
  const paramsString = location.search;
  const params = new URLSearchParams(paramsString);
  var key = params.get("key");

  const dispatch = useDispatch();

  const error = useSelector((state) =>
    _.get(state, "users.changePassword.error")
  );
  const recoveryError = useSelector((state) =>
    _.get(state, "users.changePassword.recoveryError")
  );

  const loading = useSelector((state) =>
    _.get(state, "users.changePassword.loading")
  );

  const success = useSelector((state) =>
    _.get(state, "users.changePassword.success")
  );
  const authenticationError = useSelector((state) =>
    _.get(state, "users.changePassword.authenticationError")
  );

  const [state, setState] = useState({
    error: null,
  });

  useEffect(() => {
    dispatch(validatingRecoveryLink(id, key));
  }, []);

  useEffect(() => {
    if (authenticationError && authenticationError === "account.suspended") {
      setState({
        error: "Errr. Looks like your account has been suspended.",
      });
    } else {
      setState({
        error: authenticationError,
      });
    }
  }, [authenticationError]);

  return (
    <AnonymousPageView>
      <div className={cx(styles.form)}>
        <div className={styles.logoContainer}>
          <div className={styles.logo}>
            <img src={cleverStoryLogo} alt="Cleverstory logo" />
          </div>
        </div>
        {loading && <LoadingMessage />}

        {!success && !recoveryError && !state.error && <LoadingMessage />}

        {!loading && success && !recoveryError && (
          <PasswordForm id={id} recoveryKey={key} />
        )}

        {!loading && !success && !state.error && error && (
          <ErrorMessage error={error} />
        )}

        {!loading && !success && state.error && (
          <AccountSuspensionErrorMessage />
        )}
      </div>
    </AnonymousPageView>
  );
}

const AccountSuspensionErrorMessage = () => {
  return (
    <div className={styles.suspensionError}>
      <span>Unable to reset password</span>
    </div>
  );
};

const LoadingMessage = () => {
  return (
    <div className={cx(styles.loadingContainer)}>
      <div className={styles.inner}>
        <p>Recovering Account...</p>
      </div>
    </div>
  );
};

const ErrorMessage = ({ error }) => {
  const history = useHistory();

  const handleForgotPasswordClick = () => {
    history.replace("/login/forgot-password");
  };
  return (
    <div className={styles.heading}>
      <h4>Unable to reset password</h4>
      <p>{error}</p>
      <div className={styles.buttonContainer}>
        <Button className={styles.confirm} onClick={handleForgotPasswordClick}>
          Forgot Password
        </Button>
      </div>
    </div>
  );
};

const PasswordForm = ({ id, recoveryKey }) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const changing = useSelector((state) =>
    _.get(state, "users.changePassword.changing")
  );

  const passwordErrors = useSelector((state) =>
    _.get(state, "users.changePassword.error", [])
  );

  const [state, setState] = useState({
    newPassword1: "",
    newPassword2: "",
    errors: {},
    postConfirm: false,
  });

  useEffect(() => {
    var errors = {};

    if (passwordErrors) {
      passwordErrors.forEach((error) => {
        errors[error.field] = error.message;
      });
    }

    setState((state) => {
      return {
        ...state,
        errors: errors,
      };
    });
  }, [passwordErrors]);

  const handleChange = (e) => {
    setState((state) => {
      return {
        ...state,
        [e.target.name]: e.target.value,
        errors: {},
      };
    });
  };

  const handleSigninClick = () => {
    history.replace("/login");
  };

  const handleResetPasswordClick = (event) => {
    event.preventDefault();
    if (changing) {
      return false;
    }

    let password = {
      newPassword1: state.newPassword1,
      newPassword2: state.newPassword2,
      recoveryKey: recoveryKey,
    };

    dispatch(
      resetPassword(id, password, (response) => {
        if (!changing && Object.keys(state.errors).length === 0) {
          setState((state) => {
            return {
              ...state,
              postConfirm: true,
            };
          });

          setTimeout(function () {
            history.push("/login");
          }, 9000);
        }
      })
    );
  };

  const hasFieldError = (field) => {
    return state.errors[field];
  };

  return (
    <>
      <div>
        <header className={styles.header}>Reset Password</header>



        {state.postConfirm && (
          <>
            <p>Your password changed successfully.</p>

            <div className={styles.buttonContainer}>
              <Button
                onClick={handleSigninClick}
                className={styles.loginOption}
              >
                Login
              </Button>
            </div>
          </>
        )}
      </div>

      {!state.postConfirm && (
        <form onSubmit={handleResetPasswordClick}>
          <Form.Group className={styles.resetPasswordFormGroup}>
            <Form.Control
              className={styles.resetPasswordInput}
              type="password"
              name="newPassword1"
              value={state.newPassword}
              onChange={handleChange}
              placeholder="New Password"
              isInvalid={state.errors.newPassword1}
            />
            <Form.Control.Feedback type="invalid" className={styles.error}>
              {state.errors.newPassword1 || "Password should contain atleast 8 characters"}
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Control
              className={styles.resetPasswordInput}
              type="password"
              name="newPassword2"
              value={state.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm Password"
              isInvalid={state.errors.newPassword2}
            />
            <Form.Control.Feedback type="invalid">
              {state.errors.newPassword2 || "Password should contain atleast 8 characters"}
            </Form.Control.Feedback>
          </Form.Group>

          <div onClick={handleSigninClick} className={styles.loginOption}>
            <p className={styles.signinOption}>Sign In </p>
          </div>

          <div className={styles.buttonContainer}>
            <Button type="submit" className={styles.confirm}>
              {changing ? "Processing..." : "Reset"}
            </Button>
          </div>
        </form>
      )}
    </>
  );
};
